import { z } from "zod";
import { contact } from "./site";

export type FormId =
  | "remittance"
  | "guest-feedback"
  | "job-application"
  | "work-for-stay";
export type FormValue = string | string[];
export type SubmissionData = Record<string, FormValue>;

export type FormField = {
  id: string;
  type:
    | "text"
    | "email"
    | "tel"
    | "date"
    | "time"
    | "textarea"
    | "select"
    | "checkbox"
    | "section";
  label: string;
  labelLink?: { text: string; href: string };
  description?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  exactChoices?: number;
  initialValue?: string;
  min?: string;
  max?: string;
  step?: number;
};

export type FormDefinition = {
  id: FormId;
  title: string;
  intro?: string;
  privacyNotice: string;
  fields: FormField[];
  success: string;
  successLinks?: { href: string; label: string; description: string }[];
};

const values = {
  core: [
    "親切真誠的交流 Warm and genuine communication",
    "注重環境與細節 Attention to details and environment",
    "對客人冷淡但效率高 Being cold to guests but efficient",
    "重視團隊合作 Teamwork and collaboration",
    "工作只要做好份內事就好 Only focusing on my own tasks",
  ],
};

const sharedApplicationFields: FormField[] = [
  {
    id: "name",
    type: "text",
    label: "姓名 Name",
    required: true,
    placeholder: "First and last name",
  },
  {
    id: "location",
    type: "text",
    label: "現居地",
    description: "Where do you currently live?",
    required: true,
  },
  {
    id: "birthday",
    type: "date",
    label: "生日",
    description: "Birthday",
    required: true,
  },
  { id: "email", type: "email", label: "Email", required: true },
  {
    id: "phone",
    type: "tel",
    label: "Phone",
    description:
      "台灣電話或國際格式，例如 0912-345-678、02-2345-6789、+886 912 345 678",
    placeholder: "+886 912 345 678",
    required: true,
  },
];

const storyFields: FormField[] = [
  {
    id: "background",
    type: "textarea",
    label: "背景（學經歷簡述）",
    description: "Education and Work Experience",
    required: true,
  },
  {
    id: "introduction",
    type: "textarea",
    label: "自我介紹",
    description: "We’d love to know more about you! Tell us a bit about you!",
    required: true,
  },
  {
    id: "skills",
    type: "textarea",
    label: "你有什麼特別的專長或興趣，覺得可以帶進小星？",
    description:
      "Second language / hospitality experience / any relevant talent or interest",
  },
  {
    id: "plans",
    type: "textarea",
    label: "你對未來 1–3 年有什麼規劃或目標嗎？",
    description:
      "What are your personal goals or plans for the next 1–3 years?",
  },
  {
    id: "motivation",
    type: "textarea",
    label: "請用 2–3 句話告訴我們，你為什麼想加入小星團隊？",
    description: "Why do you want to join the team?",
    required: true,
  },
  {
    id: "instagram",
    type: "text",
    label: "Instagram 帳號（非必填）",
    description: "You are welcome to share your Instagram handle.",
  },
  { id: "ideas", type: "section", label: "關於你的想法 About You" },
  {
    id: "coreValues",
    type: "checkbox",
    label: "請勾選出小星的三項核心理念",
    description: "Please select exactly 3 of our core values.",
    required: true,
    exactChoices: 3,
    options: values.core,
  },
  {
    id: "hospitalityScore",
    type: "select",
    label: "你自認是喜歡交流、樂於服務的人嗎？（5分代表完全符合）",
    description:
      "Do you consider yourself someone who enjoys connecting with people and offering hospitality?",
    required: true,
    options: ["1", "2", "3", "4", "5"],
  },
  {
    id: "detailMindset",
    type: "select",
    label: "你願意拿出職人精神、當個細節控夥伴嗎？",
    description:
      "Would you put care into the details and bring a craftsperson’s spirit to the team?",
    required: true,
    options: ["願意！ Yes!", "可能沒辦法。 I don’t think so."],
  },
];

export const formDefinitions: Record<FormId, FormDefinition> = {
  remittance: {
    id: "remittance",
    title: "入住時間回報\nArrival Information",
    intro:
      "請先告訴我們預計抵達時間。若有匯款資訊，也請一併填寫以利核對；若沒有，直接跳過即可。",
    privacyNotice:
      "我們會依個人資料蒐集告知處理入住、匯款與聯繫所需資料；送出即表示已閱讀",
    fields: [
      {
        id: "arrivalTime",
        type: "time",
        label: "預計抵達時間 Estimated Arrival Time",
        description: "台灣時間，Taiwan Time",
        required: true,
        initialValue: "16:00",
        min: "00:00",
        max: "23:50",
        step: 600,
      },
      {
        id: "bookingName",
        type: "text",
        label: "訂房姓名 Booking Name",
        required: true,
        placeholder: "務必與訂單相符",
      },
      {
        id: "email",
        type: "email",
        label: "Email",
        description: "我們會寄一封收件確認信給你。",
        required: true,
      },
      {
        id: "remainingAmount",
        type: "text",
        label: "本次匯款金額 Remittance Amount",
        description:
          "若本次沒有匯款，請跳過。Skip this field if you did not make a bank transfer.",
        placeholder: "1,400",
      },
      {
        id: "accountLastFive",
        type: "text",
        label: "匯款帳號末五碼 Last 5 digits of the remittance account number.",
        description:
          "若本次沒有匯款或沒有台灣銀行帳戶，請跳過。",
        placeholder: "例如 12345",
      },
    ],
    success:
      "資料已接收，謝謝你的回報！\n期待你在都蘭的旅程。We’re looking forward to your trip to Dulan!",
    successLinks: [
      {
        href: "/direction",
        label: "前往都蘭的交通資訊",
        description: "查看開車、公車與抵達小星的方式",
      },
      {
        href: "/life-in-dulan",
        label: "都蘭生活指南",
        description: "吃喝、散步與在地生活靈感",
      },
      {
        href: "/guest-house-info",
        label: "入住重要資訊",
        description: "入住時間、設備與住宿提醒",
      },
    ],
  },
  "guest-feedback": {
    id: "guest-feedback",
    title: "小星想聽聽你的聲音\nGuest Feedback",
    intro:
      "謝謝你來到都蘭小星！希望你在都蘭有一段舒服、放鬆的時光。很想聽聽你這次住下來的感受，不論是喜歡的地方，或覺得還可以更好的細節，都歡迎寫下來告訴我們。你的分享，會幫助小星慢慢變得更好。\n\nThank you for staying with us at Dulan Star! We hope your time in Dulan felt restful and easy. We’d love to hear about your stay—what you enjoyed and anything we could make even better. Your thoughts will help our little star keep growing and shining.",
    privacyNotice:
      "我們會依個人資料蒐集告知處理住宿回饋；送出即表示已閱讀",
    fields: [
      {
        id: "checkInDate",
        type: "date",
        label: "入住日期 Your check-in date",
        required: true,
      },
      {
        id: "roomType",
        type: "select",
        label: "入住房型 Room type",
        required: true,
        options: [
          "四人混合宿舍房 4-Bed Mixed Dorm",
          "四人女生上下舖套房｜附衛浴 4-Bed Female Dorm Ensuite",
          "四人套房附浴缸 Four-Person Suite with Bathtub",
          "都蘭小星包棟 Full Property Rental",
          "其他 Other",
        ],
      },
      {
        id: "nights",
        type: "select",
        label: "入住晚數 Length of stay",
        required: true,
        options: ["1", "2", "3", "4", "5", "More than 5"],
      },
      { id: "experience", type: "section", label: "整體體驗 Overall Experience" },
      ...[
        ["overallSatisfaction", "整體住宿滿意度 Overall satisfaction with your stay"],
        ["cleanlinessComfort", "房間整潔與舒適度 Cleanliness and comfort of the room"],
        ["sharedSpaces", "公共空間體驗 Experience in shared/common spaces"],
        ["checkinCheckout", "入住＆退房流程是否順暢 Check-in & check-out process"],
        ["staff", "與小星夥伴的互動體驗 Friendliness and helpfulness of our staff"],
        ["ambience", "空間氛圍 Ambience (lighting, scent, music, and atmosphere)"],
        ["valueForMoney", "價格是否合理 Value for money"],
      ].map(([id, label]) => ({
        id,
        type: "select" as const,
        label,
        description: "1 分代表很不滿意，5 分代表非常滿意",
        required: true,
        options: ["1", "2", "3", "4", "5"],
      })),
      {
        id: "beddingExperience",
        type: "textarea",
        label: "寢具體驗如何？ How was your bedding experience?",
        description: "枕頭、棉被與床墊 Pillow, duvet, and mattress",
        required: true,
      },
      {
        id: "issues",
        type: "textarea",
        label: "有沒有遇到任何不便、問題或建議？",
        description: "Did you encounter any inconveniences or issues?",
        required: true,
      },
      {
        id: "message",
        type: "textarea",
        label: "你想對小星說的話（選填）",
        description: "Anything else you’d like to tell us?",
      },
    ],
    success:
      "資料已接收，謝謝你願意把感受告訴我們。\n你的回饋會由小星團隊內部閱讀，幫助我們把住宿體驗做得更好。",
    successLinks: [
      {
        href: "https://maps.app.goo.gl/ozvZEqogE4NVFMMA6",
        label: "也在 Google Maps 分享體驗",
        description: "如果你願意公開分享，我們會非常感謝",
      },
      {
        href: "/life-in-dulan",
        label: "再逛逛都蘭生活",
        description: "收藏下次回來時想去的地方",
      },
    ],
  },
  "job-application": {
    id: "job-application",
    title: "Let’s work together.",
    intro:
      "Interested in working together? Fill out some info and we will be in touch shortly!",
    privacyNotice:
      "我們會依個人資料蒐集告知處理招募評估與聯繫所需資料；送出即表示已閱讀",
    fields: [
      ...sharedApplicationFields,
      {
        id: "position",
        type: "checkbox",
        label: "申請職缺 Which job are you interested in?",
        required: true,
        options: [
          "兼職管家 Part-time Guest House Host",
          "兼職清掃人員 Part-time Cleaning Staff",
        ],
      },
      {
        id: "availableFrom",
        type: "date",
        label: "可開始工作的日期",
        description: "When can you start working?",
        required: true,
      },
      {
        id: "contactTime",
        type: "select",
        label: "方便聯繫的時間",
        description: "When is the best time to contact you?",
        required: true,
        options: [
          "都可以 Anytime",
          "平日白天及週末 Weekdays (daytime) & weekends",
          "平日晚上及週末 Weekday evenings & weekends",
        ],
      },
      ...storyFields,
      {
        id: "lineAdded",
        type: "checkbox",
        label: `請加入都蘭小星的 LINE 官方帳號 ${contact.lineId}，並傳訊息給我們。`,
        labelLink: { text: contact.lineId, href: contact.line },
        description:
          "We’ll contact you via LINE about your application results and next steps.",
        required: true,
        options: ["已完成！ Done!"],
      },
    ],
    success:
      "申請完成！若有通過審核，我們將於 7 天內與你聯繫。\nThank you for taking the time to apply. If successful, we will contact you within 7 days.",
  },
  "work-for-stay": {
    id: "work-for-stay",
    title: "打工換宿 Work For Stay",
    intro: "Fill out some info and we will be in touch shortly!",
    privacyNotice:
      "我們會依個人資料蒐集告知處理換宿評估與聯繫所需資料；送出即表示已閱讀",
    fields: [
      ...sharedApplicationFields,
      {
        id: "stayFrom",
        type: "date",
        label: "換宿開始日",
        description: "When can you start working?",
        required: true,
      },
      {
        id: "stayUntil",
        type: "date",
        label: "換宿結束日",
        description: "When would you like to work until?",
        required: true,
      },
      ...storyFields,
      {
        id: "schedule",
        type: "select",
        label: "工作說明如下，你是否有信心能勝任？",
        description:
          "每週五日，每日約 4 小時。8:00 開店與公共空間維護、11:00–13:00 清潔、18:00 垃圾、21:30 閉店。",
        required: true,
        options: ["沒問題！ No problem!", "可能沒辦法。 I don’t think so."],
      },
      {
        id: "noticeAccepted",
        type: "checkbox",
        label: "注意事項 Notice",
        description:
          "認真負責且愛乾淨、能與自然及社區和諧相處、接受狗貓與戶外昆蟲；最少 14 天並有 2 天試用期。",
        required: true,
        options: ["同意 I got it"],
      },
      {
        id: "lineAdded",
        type: "checkbox",
        label: `請加入都蘭小星的 LINE 官方帳號 ${contact.lineId}，並傳訊息給我們。`,
        labelLink: { text: contact.lineId, href: contact.line },
        description:
          "We’ll contact you via LINE about your application results and next steps.",
        required: true,
        options: ["已完成！ Done!"],
      },
    ],
    success:
      "申請完成！若有通過審核，我們將於 7 天內與你聯繫。\nThank you for taking the time to apply. If successful, we will contact you within 7 days.",
  },
};

export const submissionEnvelopeSchema = z.object({
  data: z.record(
    z.string(),
    z.union([z.string().max(5000), z.array(z.string().max(500)).max(10)]),
  ),
  companyWebsite: z.string().max(0).optional().default(""),
  turnstileToken: z.string().max(2048).optional().default(""),
});

export type FieldErrors = Record<string, string>;

export function isValidPhoneNumber(input: string): boolean {
  const compact = input.trim().replace(/[\s().-]/g, "");
  const normalized = compact.startsWith("00")
    ? `+${compact.slice(2)}`
    : compact;
  const taiwanLocal = /^0(?:9\d{8}|[2-8]\d{7,8})$/;
  const international = /^\+[1-9]\d{7,14}$/;
  return taiwanLocal.test(normalized) || international.test(normalized);
}

export function validateSubmission(
  formId: FormId,
  raw: unknown,
):
  | { success: true; data: SubmissionData }
  | { success: false; fieldErrors: FieldErrors } {
  const envelope = submissionEnvelopeSchema.safeParse(raw);
  if (!envelope.success)
    return { success: false, fieldErrors: { form: "Invalid form payload." } };
  const definition = formDefinitions[formId];
  const fieldErrors: FieldErrors = {};
  const data = envelope.data.data;

  for (const field of definition.fields) {
    if (field.type === "section") continue;
    const value = data[field.id];
    const empty =
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);
    if (field.required && empty) {
      fieldErrors[field.id] = "此欄位為必填 Required";
      continue;
    }
    if (empty) continue;
    if (
      field.type === "email" &&
      typeof value === "string" &&
      !z.string().email().safeParse(value.trim()).success
    )
      fieldErrors[field.id] = "請輸入有效的 Email 格式";
    if (
      field.type === "tel" &&
      typeof value === "string" &&
      !isValidPhoneNumber(value)
    )
      fieldErrors[field.id] = "請輸入有效的台灣或國際電話格式";
    if (field.type === "checkbox" && !Array.isArray(value))
      fieldErrors[field.id] = "請選擇至少一個選項";
    if (field.options) {
      const selected = Array.isArray(value) ? value : [value];
      if (selected.some((item) => !field.options?.includes(item)))
        fieldErrors[field.id] = "包含無效選項";
    }
    if (
      field.exactChoices &&
      (!Array.isArray(value) || value.length !== field.exactChoices)
    )
      fieldErrors[field.id] = `請選擇 ${field.exactChoices} 個選項`;
  }

  if (formId === "remittance") {
    const amount = data.remainingAmount;
    const normalizedAmount = typeof amount === "string" ? amount.trim() : "";
    if (
      normalizedAmount &&
      (!/^\d+$/.test(normalizedAmount) ||
        !Number.isSafeInteger(Number(normalizedAmount)))
    )
      fieldErrors.remainingAmount = "請輸入有效的非負整數金額";
    const account = data.accountLastFive;
    if (typeof account === "string" && account && !/^\d{5}$/.test(account))
      fieldErrors.accountLastFive = "請輸入帳號末五碼";
    const time = data.arrivalTime;
    if (
      typeof time === "string" &&
      time &&
      !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)
    )
      fieldErrors.arrivalTime = "請輸入有效時間";
  }
  if (formId === "work-for-stay") {
    const from = data.stayFrom;
    const until = data.stayUntil;
    if (
      typeof from === "string" &&
      typeof until === "string" &&
      from &&
      until &&
      until < from
    )
      fieldErrors.stayUntil = "結束日不可早於開始日";
  }

  return Object.keys(fieldErrors).length
    ? { success: false, fieldErrors }
    : { success: true, data };
}
